import React, { useEffect, useRef, useState } from 'react';
import { HighlightOffTwoTone } from '@mui/icons-material';
import { FormControl, FormHelperText, IconButton, OutlinedInput } from '@mui/material';
import type { TextFieldWithWarningProps, TextInputFieldProps } from '@/interfaces/component/textField.interface';
import FlexBox from '@/components/FlexBox';
import theme from '@/app/theme';

const CustomHelperText = ({ helpMessage }: { helpMessage: string }) => {
  return helpMessage !== '' ? (
    <FormHelperText
      sx={{
        marginX: 0
      }}
    >
      {helpMessage}
    </FormHelperText>
  ) : (
    ''
  );
};

const TextFieldWithMessage = ({
  placeholder,
  onChange,
  onKeyDown,
  helpMessage = '',
  fullWidth = true,
  validation,
  isError = false,
  clear,
  endAdornment,
  showMessage,
  isBlurData = false,
  disabled = false,
  defaultValue
}: TextInputFieldProps & TextFieldWithWarningProps) => {
  const [error, setError] = useState(isError);
  const [isEmpty, setIsEmpty] = useState<boolean>(true);

  const inputRef = useRef(null);

  const message = React.useMemo(() => {
    return showMessage ? helpMessage : '';
  }, [showMessage, helpMessage]);

  useEffect(() => {
    if (isError !== error) {
      setError(isError);
    }
  }, [isError]);

  return (
    <FormControl error={error}>
      <OutlinedInput
        type={isBlurData ? 'password' : undefined}
        inputRef={inputRef}
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e);
          setIsEmpty((e.target.value ?? '').trim() === '');
          if (validation) {
            const isValid = validation(e);
            if (isValid === error) {
              setError(!isValid);
            }
          }
        }}
        onKeyDown={(e) => (onKeyDown !== undefined ? onKeyDown(e) : {})}
        fullWidth={fullWidth}
        sx={{
          outline: 'none',
          color: theme.palette.text.primary,
          '& fieldset': {
            outline: 'none',
            border: `1px solid ${theme.palette.gray[200]}`,
            borderRadius: '8px'
          }
        }}
        onBlur={(e) => {
          if (!e.target?.value && error) {
            setError(false);
            return;
          }

          if (validation) {
            const isValid = validation(e);

            if (isValid === error) {
              setError(!isValid);
            }
          }
        }}
        endAdornment={
          <FlexBox>
            {clear && !isEmpty ? (
              <IconButton
                onClick={() => {
                  clear();

                  if (inputRef?.current) {
                    (inputRef.current as HTMLInputElement).value = '';
                    setIsEmpty(true);
                    setError(false);
                  }
                }}
              >
                <HighlightOffTwoTone sx={{ color: theme.palette.gray[300] }} />
              </IconButton>
            ) : (
              ''
            )}
            {endAdornment ?? ''}
          </FlexBox>
        }
        disabled={disabled}
        defaultValue={defaultValue}
      />
      <CustomHelperText helpMessage={message} />
    </FormControl>
  );
};

export default TextFieldWithMessage;
